import {
    AuthUser,
    Permiss
} from 'contexts/auth/interface'
import jwt_decode from 'jwt-decode'
import {
    AuthRequestDTO,
    AuthTokens
} from 'models'
import AuthService from 'services/auth'
import { getDifferenceInMinutes } from 'utils/date'
import { hasPermissionBeforeLog } from 'utils/permissions/PermissionGate'
import { permissions } from 'utils/permissions/permissionsMap'

let refreshTokenPromise: Promise<void> | null = null

export class AuthDomain {
    async refreshToken(): Promise<void> {
        const refreshToken =
            sessionStorage.getItem('refreshToken') || ''

        if (!refreshToken) {
            return
        }

        if (!refreshTokenPromise) {
            refreshTokenPromise = (async () => {
                try {
                    const { data: tokens } =
                        await AuthService.refreshToken({
                            token: refreshToken
                        })

                    this.saveTokens(tokens)
                } catch (error) {
                    console.error(
                        'Erro ao renovar token',
                        error
                    )
                }
            })().finally(() => {
                refreshTokenPromise = null
            })
        }

        return refreshTokenPromise
    }

    async makeRefreshToken(): Promise<void> {
        const dateGenerateToken =
            sessionStorage.getItem(
                'date-generate-token'
            )

        if (!dateGenerateToken) {
            return
        }

        const convertToDate =
            new Date(dateGenerateToken)

        if (Number.isNaN(convertToDate.getTime())) {
            return
        }

        const lastRefreshToken =
            getDifferenceInMinutes(
                convertToDate,
                new Date()
            )


        if (lastRefreshToken < 16) {
            return
        }

        await this.refreshToken()
    }

    handleLogout(isExpiredToken = false) {
        sessionStorage.clear()

        const baseURL = window.location.origin
        const loginPath = '/entrar'
        const queryParam = isExpiredToken
            ? '?session-expired=true'
            : ''

        window.location.href =
            `${baseURL}${loginPath}${queryParam}`
    }

    async signOutUser(
        isExpiredToken = false
    ): Promise<void> {
        const idToken =
            sessionStorage.getItem('idToken')

        const refreshToken =
            sessionStorage.getItem('refreshToken')

        if (!idToken || !refreshToken) {
            this.handleLogout(isExpiredToken)
            return
        }

        const decodedGuidUser =
            jwt_decode(idToken) as any

        const data = {
            usuarioId:
                decodedGuidUser[
                'custom:usuario_guid'
                ] as string,
            token: refreshToken
        }

        try {
            await AuthService.signOut(data)
        } finally {
            this.handleLogout(isExpiredToken)
        }
    }

    async signin({
        nomeUsuario,
        senha
    }: AuthRequestDTO) {
        const { data } =
            await AuthService.signIn({
                nomeUsuario,
                senha
            })

        if (!data?.session) {
            this.saveTokens(data)
        }

        return {
            isMfa: !!data?.session,
            data
        }
    }

    async signinWithCertificate(
        desafioId: string,
        assinaturaBase64: string
    ): Promise<AuthUser> {
        const { data } =
            await AuthService.signInWithCertificate({
                desafioId,
                assinaturaBase64
            })

        this.saveTokens(data)

        return data
    }

    private saveTokens(tokens: any): void {
        Object.entries(tokens).forEach(
            ([key, value]) => {
                if (
                    value !== null &&
                    value !== undefined
                ) {
                    sessionStorage.setItem(
                        key,
                        String(value)
                    )
                }
            }
        )

        sessionStorage.setItem(
            'date-generate-token',
            String(new Date())
        )
    }

    getPermissions(currentUser: AuthUser) {
        const decode =
            jwt_decode(
                currentUser.idToken
            ) as AuthTokens

        const permissionsUser =
            JSON.parse(
                decode.acessos
            ) as Permiss[]

        const mountPermissions =
            permissionsUser.flatMap(
                permission => {
                    return permission.permissoes.map(
                        out => out.scope
                    )
                }
            )

        const bk = hasPermissionBeforeLog(
            [
                permissions.usuario.leitura,
                permissions.usuario.escrita,
                permissions.instituicao.leitura,
                permissions.instituicao.escrita,
                permissions.instituicao
                    .relatorio_leitura,
                permissions.af.escrita,
                permissions.af.leitura,
                permissions.contrato.leitura,
                permissions.contrato
                    .registro_escrita,
                permissions.contrato
                    .alterar_registro_escrita,
                permissions.contrato
                    .alterar_registro_aditivo_escrita,
                permissions.relatorio.leitura,
                permissions.gestaoTaxas.leitura,
                permissions.gestaoTaxas.escrita
            ],
            mountPermissions
        )

        return {
            mountPermissions,
            bk
        }
    }
}